/**
 * Level filter for dashboard performance trends with localStorage persistence.
 *
 * @module useDashboardTrendsLevelFilter
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  readDashboardTrendsLevel,
  writeDashboardTrendsLevel,
} from '@/lib/dashboardTrendsLevelStorage';
import type { ExamLevel, SimulationResult } from '@/types/simulation-results';

const EXAM_LEVELS: readonly ExamLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

function resolveDefaultLevel(
  results: SimulationResult[],
  availableLevels: readonly ExamLevel[],
  userId: string | undefined | null,
): ExamLevel | null {
  if (availableLevels.length === 0) {
    return null;
  }
  if (availableLevels.length === 1) {
    return availableLevels[0];
  }

  const stored = readDashboardTrendsLevel(userId);
  if (stored && availableLevels.includes(stored)) {
    return stored;
  }

  const newest = [...results].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  )[0];
  if (newest && availableLevels.includes(newest.level)) {
    return newest.level;
  }

  return availableLevels[0];
}

export function useDashboardTrendsLevelFilter(
  results: SimulationResult[],
  userId: string | undefined | null,
) {
  const availableLevels = useMemo(() => {
    const levels = new Set(results.map((result) => result.level));
    return EXAM_LEVELS.filter((level) => levels.has(level));
  }, [results]);

  const [selectedLevel, setSelectedLevel] = useState<ExamLevel | null>(null);
  const prevUserIdRef = useRef(userId);

  // Initialize or repair selection when data/user changes — never override a valid in-session pick.
  useEffect(() => {
    const userChanged = prevUserIdRef.current !== userId;
    prevUserIdRef.current = userId;

    if (availableLevels.length === 0) {
      setSelectedLevel(null);
      return;
    }
    if (availableLevels.length === 1) {
      setSelectedLevel(availableLevels[0]);
      return;
    }

    setSelectedLevel((current) => {
      if (!userChanged && current && availableLevels.includes(current)) {
        return current;
      }
      return resolveDefaultLevel(results, availableLevels, userId);
    });
  }, [results, availableLevels, userId]);

  const setLevel = useCallback(
    (level: ExamLevel) => {
      setSelectedLevel(level);
      writeDashboardTrendsLevel(userId, level);
    },
    [userId],
  );

  const filteredResults = useMemo(() => {
    if (availableLevels.length <= 1) {
      return results;
    }
    if (!selectedLevel) {
      return results;
    }
    return results.filter((result) => result.level === selectedLevel);
  }, [results, selectedLevel, availableLevels.length]);

  return {
    availableLevels,
    selectedLevel,
    setLevel,
    filteredResults,
    showLevelFilter: availableLevels.length > 1,
  };
}
